import {Injectable, isDevMode} from '@angular/core';
import {AuthService} from './auth.service';
import {CommonService} from './common.service';
import {CommonDataService} from './common-data.service';
import {InflightCollectionOrdersResponse} from '../setup-payment-plan/inflight-collection-orders-response';
import {SetupPaymentPlanService} from '../setup-payment-plan/setup-payment-plan.service';
import {ActivatedRoute, Router} from '@angular/router';
import {OtpService} from '../otp/otp.service';
import {InvoiceService} from '../invoices/invoice.service';
import {LinkedAccount} from './linked-account';
import {LinkAccountService} from '../link-account/link-account.service';
import {ServiceDetails} from './service-details';
import {LinkedAccountBalanceResponse} from './linked-account-balance-response';
import {RedirectErrorDialogComponent} from '../redirect-error-dialog/redirect-error-dialog.component';
import {Constants} from './constants';
import {MatDialog} from '@angular/material/dialog';
import {BillingDetails} from "./billing-details";
import { UserLoginDetail } from './user-login-detail';
import {SiteDetails} from "./site-details";

@Injectable()
export class UserDetailsService {

  public ready: boolean = false;
  public showServiceDropdown: boolean = false;

  constructor(
    private authService: AuthService,
    private commonService: CommonService,
    public commonDataService: CommonDataService,
    private invoiceService: InvoiceService,
    private linkAccountService: LinkAccountService,
    private matDialog: MatDialog,
    private otpService: OtpService,
    private route: ActivatedRoute,
    private router: Router,
    private setupPaymentPlanService: SetupPaymentPlanService
  ) {
  }

  public async setUpCustomerDetails() {
    await this.commonService.getUserDetails().toPromise().then(async (result) => {
      if (result) {
        this.commonService.setUserDetails(result);
        await Promise.all([
          this.getServiceDetails(),
          this.getAmountOwing(),
          this.checkIfPaymentOverdue(),
          this.getLinkedAccounts(),
          this.commonService.checkInflightBpaOrders(),
          this.getBillingDetails(),
          this.commonService.getSiteDetailsByBrand(this.commonDataService.user?.migratedTo?.toUpperCase() == 'IINET' ? "migratedwestnet" : this.commonDataService.brand).subscribe((result: SiteDetails) => {
            this.commonDataService.siteDetails = result
          })
        ]).then(() => {
          this.ready = true;
        }).catch(error => {
          this.matDialog.open(RedirectErrorDialogComponent, {
            disableClose: true,
            data: Constants.SERVER_ERROR
          });
        });
      }
    }).catch(error => {
      this.matDialog.open(RedirectErrorDialogComponent, {
        disableClose: true,
        data: Constants.SERVER_ERROR
      });
    });
  }

  async getServiceDetails() {
    if (this.commonDataService.user?.migratedTo?.toUpperCase() == 'IINET') {
      await this.commonService.getMigratedServiceDetails().toPromise().then(async (result: ServiceDetails[]) => {
        this.processServiceDetails(result);
      });
    } else {
      await this.commonService.getServiceDetails().toPromise().then(async (result: ServiceDetails[]) => {
        this.processServiceDetails(result);
      });
    }
  }

  private processServiceDetails(result: ServiceDetails[]) {
    if ((result?.length > 0)) {
      this.commonDataService.serviceDetailsList = result;
      this.commonDataService.isBundleService = this.commonDataService.brand.toLowerCase() === 'iinet' &&
        this.commonDataService.serviceDetailsList?.some(service => service.serviceId && service.serviceName.toLowerCase() === 'mega bundle');

      if (this.commonDataService.serviceDetailsList?.length > 1) {
        this.commonService.selectService(result[0]);
      } else if (this.commonDataService.serviceDetailsList?.length === 1) {
        this.commonService.selectService(result[0], false);
      }
      this.showServiceDropdown = this.commonDataService.shouldShowSwitchAccount || this.commonDataService.serviceDetailsList?.length > 1;
      this.commonDataService.showMultiserviceFeature = this.commonDataService.shouldShowMultiServiceFeature();
      this.checkForHomeWirelessService();
    }
  }

  public async getAmountOwing() {
    await this.invoiceService.getLinkedAccountBalance(this.commonDataService.user.billingAccountId)
      .toPromise().then((result: LinkedAccountBalanceResponse) => {
        if (result) {
          this.commonDataService.amountOwing = result?.aggregatedBalance;
          const linkedAccountBalanceMap = new Map<string, number>();
          result?.balanceData?.forEach(account => linkedAccountBalanceMap.set(account.billingAccountId, account.balance));
          this.commonDataService.linkedAccountBalanceMap = linkedAccountBalanceMap;
        } else {
          console.log('unable to retrieve amount owing: no invoices');
        }
      }, error => {
        console.log('unable to retrieve amount owing: ' + error);
      });
  }

  public async getLinkedAccounts() {
    await this.linkAccountService.getLinkedAccounts(this.commonDataService.user.billingAccountId)
      .toPromise().then((result: LinkedAccount[]) => {
        this.commonDataService.linkedAccounts = result;
        this.commonDataService.checkShouldShowSwitchAccount();
      });
  }

  private async checkIfPaymentOverdue() {
    await this.commonService.checkIfCollectionsOrderInflight()
      .toPromise().then((res: InflightCollectionOrdersResponse) => {
        if (isDevMode()) {
          this.commonDataService.hasExistingPaymentPlan = res.hasExistingPaymentPlan;
          this.commonDataService.hasInflightCollectionsOrder = res.hasInflightCollectionsOrder;
          if (res.hasInflightCollectionsOrder) {
            this.setupPaymentPlanService.collectionsOrderId = res.orderId;
          }
        }
        this.commonDataService.hasOverduePayment = res.hasInflightCollectionsOrder;
      }, () => {
        console.log('unable to check if customer has payment overdue');
      });
  }

  private checkForHomeWirelessService() {
    this.commonDataService.hasHomeWirelessService = this.commonDataService.serviceDetailsList.some(serviceDetail =>
      (serviceDetail.category === 'HOME_WIRELESS'));
  }
  async getBillingDetails() {
    await  this.commonService.getBillingAccountDetails().toPromise().then(async (result: BillingDetails) => {
      if (result) {
        this.commonDataService.billingAccountDetails = result
      }
    });
  }

  async checkIfFirstTime() {
    if (!this.commonDataService.csrMode) {
      await this.commonService.getUserLoginDetail().toPromise().then((result: UserLoginDetail) => {
        if (result.firstLogin) {
          this.commonDataService.isFirstTimeLogin = result.firstLogin;
          window.location.replace("/setUpNewPassword?username=" + this.commonDataService.user?.userId + "&firstTimeLogin=true&brand="+this.commonDataService.brand);
        }
      }).catch(error => {
        this.matDialog.open(RedirectErrorDialogComponent, {
          disableClose: true,
          data: Constants.SERVER_ERROR
        });
      });
    }
  }

}
